import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Chip,
  CircularProgress, Alert, Grid, Table, TableBody, TableCell,
  TableRow, AppBar, Toolbar, IconButton, Avatar
} from '@mui/material';
import {
  Download as DownloadIcon, Logout as LogoutIcon,
  MonitorHeart, FavoriteBorder, LocalHospital
} from '@mui/icons-material';
import { reportAPI } from '../services/api';
import { generateReport } from '../utils/generateReport';
import toast from 'react-hot-toast';

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';

function VitalChip({ label, value, unit, alert }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 2, border: `1px solid ${alert ? '#fee2e2' : '#e0f2fe'}`,
      bgcolor: alert ? '#fff7f7' : '#f0f9ff', textAlign: 'center'
    }}>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{label}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, color: alert ? '#dc2626' : DARK_BLUE }}>
        {value ?? '—'}
      </Typography>
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>{unit}</Typography>
    </Box>
  );
}

function ReportCard({ report, isLatest, patientInfo }) {
  const vitals = report.vitals || report.testData || {};

  const handleDownload = () => {
    try {
      generateReport({
        patient: patientInfo,
        session: report._rawSession || null,
        testData: report._rawTestData || {},
        legacyVitals: report._rawLegacy || null,
        reportGeneratedAt: new Date().toISOString(),
        maskPii: false,
      });
    } catch (e) {
      toast.error('Failed to generate PDF');
    }
  };

  return (
    <Card sx={{
      mb: 2, borderRadius: 3,
      boxShadow: isLatest ? `0 4px 24px rgba(0,168,232,0.15)` : '0 2px 12px rgba(0,0,0,0.06)',
      border: isLatest ? `2px solid ${BLUE}` : '1px solid #e2e8f0'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE }}>
                Health Report
              </Typography>
              {isLatest && <Chip label="Latest" size="small" sx={{ bgcolor: BLUE, color: '#fff', fontWeight: 700, fontSize: '0.65rem' }} />}
            </Box>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {report.testDate || report.createdAt
                ? new Date(report.testDate || report.createdAt).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })
                : 'Date unavailable'}
            </Typography>
          </Box>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            size="small"
            onClick={handleDownload}
            sx={{
              borderColor: BLUE, color: BLUE, borderRadius: 2, fontWeight: 600,
              '&:hover': { bgcolor: '#f0f9ff' }
            }}
          >
            Download PDF
          </Button>
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <VitalChip label="Blood Pressure" value={vitals.systolic && vitals.diastolic ? `${vitals.systolic}/${vitals.diastolic}` : null} unit="mmHg" alert={vitals.systolic >= 140} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <VitalChip label="Heart Rate" value={vitals.heartRate} unit="bpm" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <VitalChip label="Oxygen Saturation (SpO2)" value={vitals.oxygenSaturation} unit="%" alert={vitals.oxygenSaturation && vitals.oxygenSaturation < 95} />
          </Grid>
          <Grid item xs={6} sm={3}>
            <VitalChip label="BMI" value={vitals.bmi?.toFixed(1)} unit="kg/m²" />
          </Grid>
        </Grid>

        {/* Detail table */}
        <Table size="small">
          <TableBody>
            {[
              ['Height', vitals.height, 'cm'],
              ['Weight', vitals.weight, 'kg'],
              ['Body Fat %', vitals.bodyFatPercentage, '%'],
              ['Hemoglobin', vitals.hemoglobin, 'g/dL'],
              ['Fasting Glucose', vitals.bloodGlucose?.fasting ?? vitals.bloodGlucoseFasting, 'mg/dL'],
              ['Pulse Rate (SpO2 Device)', vitals.pulseRate, 'bpm'],
              ['Respiratory Rate', vitals.respiratoryRate, 'breaths/min'],
              ['Body Temperature', vitals.bodyTemperature, '°F'],
              ['Spirometry - FVC', vitals.fvc, 'L'],
              ['Spirometry - FEV1', vitals.fev1, 'L'],
            ].filter(([, v]) => v !== undefined && v !== null).map(([label, value, unit]) => (
              <TableRow key={label} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                <TableCell sx={{ color: '#64748b', fontSize: '0.85rem', border: 'none', py: 0.5 }}>{label}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: DARK_BLUE, border: 'none', py: 0.5 }}>
                  {value} <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{unit}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Disclaimer */}
        <Box sx={{
          mt: 2, p: 2, bgcolor: '#fffbeb', borderRadius: 2,
          border: '1px solid #fde68a',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Confidential Medical Report Disclaimer
          </Typography>
          <Typography variant="caption" sx={{ color: '#78350f', lineHeight: 1.7, display: 'block', fontSize: '0.7rem' }}>
            This report has been prepared based on the specimen(s), data, and/or information provided at the time of evaluation and is intended strictly for clinical reference and informational purposes only. The findings and results contained herein must be interpreted in conjunction with the patient's clinical history, physical examination, and other diagnostic parameters by a qualified and registered medical practitioner. While every effort has been made to ensure the accuracy and reliability of the results, the reporting laboratory/organization does not assume responsibility for variations that may arise due to pre-analytical, analytical, or post-analytical factors including, but not limited to, sample collection, handling, biological variability, or technical limitations. Reference ranges provided are indicative and may vary depending on demographic and physiological factors. This report shall not be considered as a standalone basis for diagnosis, treatment, or medical decision-making. Any medical action taken solely on the basis of this report shall be at the discretion and responsibility of the consulting healthcare provider. All patient-related information is maintained with strict confidentiality in accordance with applicable Indian laws, including the Information Technology Act and Digital Personal Data Protection regulations, and aligned with internationally accepted healthcare privacy standards. Unauthorized use, disclosure, or reproduction of this report is strictly prohibited and may be subject to legal action. The issuing entity shall not be held liable for any direct or indirect consequences arising from the use or interpretation of this report without appropriate medical consultation. This is a system-generated report and may not require a physical signature unless otherwise specified.
          </Typography>
        </Box>

        {/* Doctor's Recommendation */}
        <Box sx={{ mt: 2 }}>
          {report.recommendation?.text ? (
            <Box sx={{
              p: 2, bgcolor: '#f0fdf4', borderRadius: 2,
              border: '1px solid #bbf7d0',
              display: 'flex', gap: 1.5, alignItems: 'flex-start'
            }}>
              <LocalHospital sx={{ color: '#16a34a', mt: 0.3, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#15803d', fontWeight: 700, mb: 0.5 }}>
                  Doctor's Recommendation
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.7 }}>
                  {report.recommendation.text}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 1 }}>
                  — Dr. {report.recommendation.doctorName || 'NA'}
                  {report.recommendation.doctorDesignation
                    ? `, ${report.recommendation.doctorDesignation}` : ''}
                  {report.recommendation.addedAt
                    ? ` · ${new Date(report.recommendation.addedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{
              p: 1.5, bgcolor: '#f8fafc', borderRadius: 2,
              border: '1px dashed #e2e8f0', textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                Doctor's Recommendation: <strong>NA</strong>
              </Typography>
            </Box>
          )}
        </Box>

      </CardContent>
    </Card>
  );
}

export default function PatientPortal({ onLogout }) {
  const [reports, setReports] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const mobile = JSON.parse(localStorage.getItem('user') || 'null')?.mobile ||
    (() => {
      try {
        const payload = JSON.parse(atob(localStorage.getItem('patientToken')?.split('.')[1] || ''));
        return payload.mobile;
      } catch { return null; }
    })();

  useEffect(() => {
    if (!mobile) {
      navigate('/patient-login');
      return;
    }
    reportAPI.getAllByMobile(mobile)
      .then(({ data: responseBody }) => {
        const apiData = responseBody.data || responseBody;

        // If name/mobile came back still encrypted (hex:hex), fall back to plain-text sources
        const isEncrypted = (v) => typeof v === 'string' && /^[0-9a-f]{32}:[0-9a-f]+$/i.test(v);
        const rawInfo = apiData.patient || {};
        const fallbackName = apiData.legacyReports?.[0]?.patientName || null;
        setPatientInfo({
          ...rawInfo,
          name: isEncrypted(rawInfo.name) ? (fallbackName || 'Patient') : (rawInfo.name || fallbackName || 'Patient'),
          mobile: isEncrypted(rawInfo.mobile) ? mobile : (rawInfo.mobile || mobile),
        });

        const sessions = (apiData.reports || []).map(s => {
          const td = s.testData || {};
          return {
            ...s,
            recommendation: s.recommendation || null,
            testDate: s.startedAt || s.completedAt,
            _rawTestData: td,
            _rawSession: {
              sessionId: s.sessionId,
              status: s.status,
              location: s.location,
              startedAt: s.startedAt,
              completedAt: s.completedAt,
              totalStations: s.totalStations,
              completedStations: s.completedStations,
            },
            testData: {
              systolic: td.bloodPressure?.systolic,
              diastolic: td.bloodPressure?.diastolic,
              heartRate: td.heartRate?.value,
              pulseRate: td.heartRate?.value,
              oxygenSaturation: td.oxygenSaturation?.value,
              bodyTemperature: td.bodyTemperature?.value,
              weight: td.weight?.value,
              height: td.height?.value,
              bmi: td.bmi?.value,
              bloodGlucoseFasting: td.bloodGlucoseFasting?.value,
              hemoglobin: td.hemoglobin?.value,
              respiratoryRate: td.respiratoryRate?.value,
            }
          };
        });

        const legacy = (apiData.legacyReports || []).map(v => ({
          sessionId: v._id,
          testDate: v.testDate || v.createdAt,
          status: 'completed',
          _rawLegacy: v,
          testData: {
            systolic: v.bloodPressure?.systolic,
            diastolic: v.bloodPressure?.diastolic,
            heartRate: v.heartRate?.value,
            pulseRate: v.pulseRate?.value,
            oxygenSaturation: v.oxygenSaturation?.value,
            bodyTemperature: v.bodyTemperature?.value,
            weight: v.weight?.value,
            height: v.height?.value,
            bmi: v.bmi?.value,
            bloodGlucose: { fasting: v.bloodGlucose?.fasting, random: v.bloodGlucose?.random },
            hemoglobin: v.hemoglobin?.value,
            bodyFatPercentage: v.bodyComposition?.bodyFatPercentage,
            fvc: v.spirometry?.fvc,
            fev1: v.spirometry?.fev1,
          }
        }));
        setReports([...sessions, ...legacy]);
      })
      .catch(err => setError('Could not load your reports. ' + (err.response?.data?.message || '')))
      .finally(() => setLoading(false));
  }, [mobile, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('patientToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    if (onLogout) onLogout();
    navigate('/patient-login');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f9ff' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{
        background: `linear-gradient(135deg, ${DARK_BLUE}, ${BLUE})`,
      }}>
        <Toolbar>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', mr: 1.5
          }}>
            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 13 }}>EW</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
              Eros Wellness
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Patient Health Portal
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', fontSize: '0.8rem' }}>
              {mobile?.[0] || 'P'}
            </Avatar>
            <Typography variant="body2" sx={{ color: '#fff', display: { xs: 'none', sm: 'block' } }}>
              +91 {mobile}
            </Typography>
            <IconButton onClick={handleLogout} sx={{ color: '#fff' }}>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        {/* Welcome Banner */}
        <Card sx={{
          mb: 3, borderRadius: 3,
          background: `linear-gradient(135deg, ${DARK_BLUE} 0%, ${BLUE} 60%, ${GREEN} 100%)`,
          color: '#fff'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <MonitorHeart sx={{ fontSize: 40, opacity: 0.9 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Your Health Reports
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {reports.length > 0
                    ? `${reports.length} report${reports.length > 1 ? 's' : ''} on record`
                    : 'No reports yet — visit a kiosk to get started'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: BLUE }} />
          </Box>
        ) : reports.length === 0 ? (
          <Card sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
            <FavoriteBorder sx={{ fontSize: 64, color: '#dbeafe', mb: 2 }} />
            <Typography variant="h6" sx={{ color: DARK_BLUE, fontWeight: 700 }}>No Reports Found</Typography>
            <Typography sx={{ color: '#64748b', mt: 1 }}>
              Visit an Eros Wellness kiosk to get your first health checkup.
            </Typography>
          </Card>
        ) : (
          reports.map((report, i) => (
            <ReportCard key={report._id || report.sessionId || i} report={report} isLatest={i === 0} patientInfo={patientInfo} />
          ))
        )}
      </Box>
    </Box>
  );
}
